import { Variant, Variants } from '../types/variant';

const pages = {
  CONSULTATION: {
    title: 'Consultation',
    sections: ['CONTEXTE', 'SOM_MENSURATION'],
    progressSteps: [1, 2, 4, 6],
  },
  COMPLAINTS: {
    title: 'Motifs',
    sections: [
      'MOTIF',
      'SYMPTÔMES',
      'SC_FOLLOWUP',
      'SC_TEMPORALITY',
      'ORL',
      'FEVER',
      'MOTIF_SYSTEMATIC',
      'DEPUIS_CARDIO',
      'SUIVI_CARDIO',
      'QOL',
      'MOTIF_GYNECO',
      'MAIN_MOTIF',
    ],
    progressSteps: [
      8, 16, 19, 22, 25, 28, 31, 34, 37, 40, 43, 46, 52, 55, 58, 61, 64, 67, 70,
      73, 76,
    ],
  },
  HISTORY: {
    title: 'Antécédents',
    sections: [
      'ANTECEDENTS', // deprecated after KB PR #846 is deployed
      'ATCD_NV_PATIENT',
      'ATCD_NV_PATIENT_FOLLOWUP',
      'ANTECEDENTS_GYNECO_NV_PATIENT',
      'ATCD_HEADER',
      'ATCD',
      'ANTECEDENTS_GYNECO',
      'ALD_DM',
      'ALLERMED',
      'TRAITEMENTS',
      'ATCD_MISC',
      'MENSURATION',
    ],
    progressSteps: [73, 75, 77, 79, 81, 83],
  },
  PREVENTION: {
    title: 'Prévention',
    sections: [
      'PREVENTION_HEADER',
      'PEDIATRIC_PREVENTION',
      'PEDIATRIC_NUTRITION',
      'PEDIATRIC_STOOLS',
      'PEDIATRIC_SLEEP',
      'PEDIATRIC_SCREEN',
      'PEDIATRIC_AUDITION',
      'PEDIATRIC_DEVELOPMENT',
      'PEDIATRIC_ACTIVITY',
      'PEDIATRIC_EDUCATION',
      'PEDIATRIC_MENTAL',
      'PEDIATRIC_HEARING',
      'PEDIATRIC_VISION',
      'PEDIATRIC_SCREENING',
      'TOBACCO_MANDATORY',
      'ALCOHOL_MANDATORY',
      'ACTIVITY_MANDATORY',
      'VIOLENCE_MANDATORY',
      'SLEEP_MANDATORY',
      'MENTAL_MANDATORY',
      'VACCINES_MANDATORY',
      'OPTIONAL_SCREENING',
      'SCREENING_DENTAL',
      'SCREENING_DERMA',
      'SCREENING_COLORECTAL',
      'SCREENING_BREAST',
      'SCREENING_CERVIX',
      'CONSENT_PREVENTION',
    ],
    progressSteps: [
      84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99,
    ],
  },
  CONCLUSION: {
    title: 'Conclusion',
    sections: ['RECAP'],
    progressSteps: [100],
  },
};

const DEFAULT: Variant = {
  name: 'Médecine générale',
  status: 'stable',
  availableInDemo: false,
  description:
    'Questionnaire pour les patients de médecine générale. Couvre les motifs, les antécédents personnels et familiaux du patient ainsi que la prévention.',
  speciality: 'MED-G',
  patientOnboarding: 'MED_G',
  patientOffboarding: 'DEFAULT',
  emailTemplate: 'MED_G_DEFAULT',
  reportTemplate: 'MED_G_DEFAULT',
  initialQuestions: [] as string[],
  pages,
  initialAssertions: [
    "[Message: #‹]-(06.a_lieu_emission)->[Lieu]-(01.DESCR)->[Salle d'Attente]-(PART_OF)->[Cabinet Médical: #c]",
    '[Médecin: #z‹]-(a_spécialité)->[Médecine Générale]',
    '[Médecin: #z‹]-(a_habilitation)->[IVG médicamenteuse]',
  ],
  options: ['COMPACT', 'ALERTES'],
  personalizableOptions: {
    MOTIVES_ENABLED_NEW_PATIENT: { default: true, duration: 3 },
    MOTIVES_ENABLED_EXISTING_PATIENT: { default: true, duration: 3 },
    MOTIVES_FREE_TEXT_NEW_PATIENT: { default: false, duration: 1 },
    MOTIVES_FREE_TEXT_EXISTING_PATIENT: { default: false, duration: 1 },
    MEDICAL_HISTORY_ENABLED_NEW_PATIENT: { default: true, duration: 1 },
    MEDICAL_HISTORY_ENABLED_EXISTING_PATIENT: { default: true, duration: 1 },
    PREVENTION_TOBACCO_ENABLED_NEW_PATIENT: { default: true, duration: 0.2 },
    PREVENTION_TOBACCO_ENABLED_EXISTING_PATIENT: {
      default: true,
      duration: 0.2,
    },
    PREVENTION_TOBACCO_REQUIRED_NEW_PATIENT: { default: false, duration: 0.75 },
    PREVENTION_TOBACCO_REQUIRED_EXISTING_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_ALCOHOL_ENABLED_NEW_PATIENT: { default: true, duration: 0.2 },
    PREVENTION_ALCOHOL_ENABLED_EXISTING_PATIENT: {
      default: true,
      duration: 0.2,
    },
    PREVENTION_ALCOHOL_REQUIRED_NEW_PATIENT: { default: false, duration: 0.75 },
    PREVENTION_ALCOHOL_REQUIRED_EXISTING_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_MENTAL_ENABLED_NEW_PATIENT: { default: true, duration: 0.2 },
    PREVENTION_MENTAL_ENABLED_EXISTING_PATIENT: {
      default: true,
      duration: 0.2,
    },
    PREVENTION_MENTAL_REQUIRED_NEW_PATIENT: { default: false, duration: 0.75 },
    PREVENTION_MENTAL_REQUIRED_EXISTING_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_SLEEP_ENABLED_NEW_PATIENT: { default: true, duration: 0.2 },
    PREVENTION_SLEEP_ENABLED_EXISTING_PATIENT: { default: true, duration: 0.2 },
    PREVENTION_SLEEP_REQUIRED_NEW_PATIENT: { default: false, duration: 0.75 },
    PREVENTION_SLEEP_REQUIRED_EXISTING_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_VACCINES_ENABLED_NEW_PATIENT: { default: true, duration: 0.2 },
    PREVENTION_VACCINES_ENABLED_EXISTING_PATIENT: {
      default: true,
      duration: 0.2,
    },
    PREVENTION_VACCINES_REQUIRED_NEW_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_VACCINES_REQUIRED_EXISTING_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_ACTIVITY_ENABLED_NEW_PATIENT: { default: true, duration: 0.2 },
    PREVENTION_ACTIVITY_ENABLED_EXISTING_PATIENT: {
      default: true,
      duration: 0.2,
    },
    PREVENTION_ACTIVITY_REQUIRED_NEW_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_ACTIVITY_REQUIRED_EXISTING_PATIENT: {
      default: false,
      duration: 0.75,
    },
    SCREENING_TRAUMA_ENABLED_NEW_PATIENT: { default: true, duration: 0.15 },
    SCREENING_TRAUMA_ENABLED_EXISTING_PATIENT: {
      default: true,
      duration: 0.15,
    },
    SCREENING_COLORECTAL_ENABLED_NEW_PATIENT: { default: true, duration: 0.15 },
    SCREENING_COLORECTAL_ENABLED_EXISTING_PATIENT: {
      default: true,
      duration: 0.15,
    },
    SCREENING_CERVICAL_HPV_ENABLED_NEW_PATIENT: {
      default: true,
      duration: 0.15,
    },
    SCREENING_CERVICAL_HPV_ENABLED_EXISTING_PATIENT: {
      default: true,
      duration: 0.15,
    },
    SCREENING_BREAST_ENABLED_NEW_PATIENT: { default: true, duration: 0.15 },
    SCREENING_BREAST_ENABLED_EXISTING_PATIENT: {
      default: true,
      duration: 0.15,
    },
    SCREENING_DENTAL_ENABLED_NEW_PATIENT: { default: true, duration: 0.15 },
    SCREENING_DENTAL_ENABLED_EXISTING_PATIENT: {
      default: true,
      duration: 0.15,
    },
    SCREENING_DERMATOLOGY_ENABLED_NEW_PATIENT: {
      default: true,
      duration: 0.15,
    },
    SCREENING_DERMATOLOGY_ENABLED_EXISTING_PATIENT: {
      default: true,
      duration: 0.15,
    },
    SCREENING_COPD_ENABLED_NEW_PATIENT: { default: true, duration: 0.15 },
    SCREENING_COPD_ENABLED_EXISTING_PATIENT: { default: true, duration: 0.15 },
    // Hidden flag (ALDO-2867): not exposed in the pro personalization modal,
    // switched on from Forest only. Skips the KB question #MedTT0101 (« retour
    // au médecin traitant ? ») and its report line. Declared here so the API
    // validator accepts the key when the pro saves its personalization.
    MEDECIN_TRAITANT_FEEDBACK_HIDDEN: { default: false, duration: 0 },
  },
};

const snp: Variant = {
  name: 'Soins non programmés',
  status: 'stable',
  availableInDemo: true,
  description:
    'Questionnaire adapté aux consultations sans rendez-vous ou urgences légères. Le patient sélectionne un seul motif parmi une liste ciblée pour permettre un recueil rapide et efficace avant la prise en charge.',
  speciality: 'MED-G',
  patientOnboarding: 'MED_G',
  patientOffboarding: 'DEFAULT',
  emailTemplate: 'MED_G_DEFAULT',
  reportTemplate: 'MED_G_DEFAULT',
  initialQuestions: [],
  pages,
  initialAssertions: [
    "[Message: #‹]-(06.a_lieu_emission)->[Lieu]-(01.DESCR)->[Salle d'Attente]-(PART_OF)->[Cabinet Médical: #c]",
    '[Médecin: #z‹]-(a_spécialité)->[Médecine Générale]',
    '[Médecin: #z‹]-(a_habilitation)->[IVG médicamenteuse]',
  ],
  options: ['COMPACT', 'ALERTES', 'SINGLE_MOTIVE', 'LIMITED_MOTIVES'],
  personalizableOptions: {
    MOTIVES_ENABLED_NEW_PATIENT: { default: true, duration: 3 },
    MOTIVES_ENABLED_EXISTING_PATIENT: { default: true, duration: 3 },
    MOTIVES_FREE_TEXT_NEW_PATIENT: { default: false, duration: 1 },
    MOTIVES_FREE_TEXT_EXISTING_PATIENT: { default: false, duration: 1 },
    MEDICAL_HISTORY_ENABLED_NEW_PATIENT: { default: true, duration: 1 },
    MEDICAL_HISTORY_ENABLED_EXISTING_PATIENT: { default: true, duration: 1 },
    PREVENTION_TOBACCO_ENABLED_NEW_PATIENT: { default: false, duration: 0.2 },
    PREVENTION_TOBACCO_ENABLED_EXISTING_PATIENT: {
      default: false,
      duration: 0.2,
    },
    PREVENTION_TOBACCO_REQUIRED_NEW_PATIENT: { default: false, duration: 0.75 },
    PREVENTION_TOBACCO_REQUIRED_EXISTING_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_ALCOHOL_ENABLED_NEW_PATIENT: { default: false, duration: 0.2 },
    PREVENTION_ALCOHOL_ENABLED_EXISTING_PATIENT: {
      default: false,
      duration: 0.2,
    },
    PREVENTION_ALCOHOL_REQUIRED_NEW_PATIENT: { default: false, duration: 0.75 },
    PREVENTION_ALCOHOL_REQUIRED_EXISTING_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_MENTAL_ENABLED_NEW_PATIENT: { default: false, duration: 0.2 },
    PREVENTION_MENTAL_ENABLED_EXISTING_PATIENT: {
      default: false,
      duration: 0.2,
    },
    PREVENTION_MENTAL_REQUIRED_NEW_PATIENT: { default: false, duration: 0.75 },
    PREVENTION_MENTAL_REQUIRED_EXISTING_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_SLEEP_ENABLED_NEW_PATIENT: { default: false, duration: 0.2 },
    PREVENTION_SLEEP_ENABLED_EXISTING_PATIENT: {
      default: false,
      duration: 0.2,
    },
    PREVENTION_SLEEP_REQUIRED_NEW_PATIENT: { default: false, duration: 0.75 },
    PREVENTION_SLEEP_REQUIRED_EXISTING_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_VACCINES_ENABLED_NEW_PATIENT: { default: false, duration: 0.2 },
    PREVENTION_VACCINES_ENABLED_EXISTING_PATIENT: {
      default: false,
      duration: 0.2,
    },
    PREVENTION_VACCINES_REQUIRED_NEW_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_VACCINES_REQUIRED_EXISTING_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_ACTIVITY_ENABLED_NEW_PATIENT: { default: false, duration: 0.2 },
    PREVENTION_ACTIVITY_ENABLED_EXISTING_PATIENT: {
      default: false,
      duration: 0.2,
    },
    PREVENTION_ACTIVITY_REQUIRED_NEW_PATIENT: {
      default: false,
      duration: 0.75,
    },
    PREVENTION_ACTIVITY_REQUIRED_EXISTING_PATIENT: {
      default: false,
      duration: 0.75,
    },
    SCREENING_TRAUMA_ENABLED_NEW_PATIENT: { default: false, duration: 0.15 },
    SCREENING_TRAUMA_ENABLED_EXISTING_PATIENT: {
      default: false,
      duration: 0.15,
    },
    SCREENING_COLORECTAL_ENABLED_NEW_PATIENT: {
      default: false,
      duration: 0.15,
    },
    SCREENING_COLORECTAL_ENABLED_EXISTING_PATIENT: {
      default: false,
      duration: 0.15,
    },
    SCREENING_CERVICAL_HPV_ENABLED_NEW_PATIENT: {
      default: false,
      duration: 0.15,
    },
    SCREENING_CERVICAL_HPV_ENABLED_EXISTING_PATIENT: {
      default: false,
      duration: 0.15,
    },
    SCREENING_BREAST_ENABLED_NEW_PATIENT: { default: false, duration: 0.15 },
    SCREENING_BREAST_ENABLED_EXISTING_PATIENT: {
      default: false,
      duration: 0.15,
    },
    SCREENING_DENTAL_ENABLED_NEW_PATIENT: { default: false, duration: 0.15 },
    SCREENING_DENTAL_ENABLED_EXISTING_PATIENT: {
      default: false,
      duration: 0.15,
    },
    SCREENING_DERMATOLOGY_ENABLED_NEW_PATIENT: {
      default: false,
      duration: 0.15,
    },
    SCREENING_DERMATOLOGY_ENABLED_EXISTING_PATIENT: {
      default: false,
      duration: 0.15,
    },
    SCREENING_COPD_ENABLED_NEW_PATIENT: { default: false, duration: 0.15 },
    SCREENING_COPD_ENABLED_EXISTING_PATIENT: { default: false, duration: 0.15 },
    // Hidden flag (ALDO-2867): not exposed in the pro personalization modal,
    // switched on from Forest only. Skips the KB question #MedTT0101 (« retour
    // au médecin traitant ? ») and its report line. Declared here so the API
    // validator accepts the key when the pro saves its personalization.
    MEDECIN_TRAITANT_FEEDBACK_HIDDEN: { default: false, duration: 0 },
  },
  tag: 'SNP',
};

export const medGVariants = {
  DEFAULT,
  snp,
  snp_demo: {
    name: 'Médecine générale SNP (démo)',
    status: 'restricted',
    description:
      'Questionnaire de démonstration pour les soins non programmés en médecine générale. Version allégée avec onboarding simplifié, motif unique et prévention réduite, destinée à la présentation du produit.',
    speciality: 'MED-G',
    patientOnboarding: 'SNP_DEMO',
    patientOffboarding: 'CONFIRMATION_ONLY',
    emailTemplate: 'MED_G_DEFAULT',
    reportTemplate: 'MED_G_DEFAULT',
    initialQuestions: [],
    pages,
    initialAssertions: [
      "[Message: #‹]-(06.a_lieu_emission)->[Lieu]-(01.DESCR)->[Salle d'Attente]-(PART_OF)->[Cabinet Médical: #c]",
      '[Médecin: #z‹]-(a_spécialité)->[Médecine Générale]',
      '[Médecin: #z‹]-(a_habilitation)->[IVG médicamenteuse]',
    ],
    options: [
      'COMPACT',
      'ALERTES',
      'SINGLE_MOTIVE',
      'LIMITED_MOTIVES',
      'LIMITED_PREVENTION',
    ],
    tag: 'SNP',
    personalizableOptions: snp.personalizableOptions,
  },
  mmg: {
    ...snp,
    name: 'Maison médicale de garde',
    status: 'beta',
    availableInDemo: false,
    description:
      'Questionnaire pour les Maisons Médicales de Garde (MMG) en soins non programmés. Le patient flashe un QR code et répond depuis son propre téléphone. Page de consultation simplifiée et adressage activé, motif unique parmi une liste ciblée, prévention et dépistages désactivés par défaut comme en soins non programmés.',
    // ADDRESSAGE_MMG active la page de consultation MMG simplifiée + la question
    // d'adressage #MMG01 dans la KB (gardée invisible pour les autres variantes).
    options: [...snp.options, 'ADDRESSAGE_MMG'],
    // Le médecin de garde n'est pas présumé être le médecin traitant : on pose
    // #22=Non d'office (la question #22 n'est pas affichée en MMG). Les chaînes
    // miroitent exactement la réponse 22-02 de la KB (KB-CONTEXTE.mdkg l. 414).
    initialAssertions: [
      ...snp.initialAssertions,
      '[Conjonction: #1‹]-(ET)->[Assertion: #12‹]',
      '[Assertion: #12‹]-(01.DESCR)->[Médecin: #z]-(---¬)->[Médecin: #]-(rôle)->[Médecin Traitant: #]-(REL)->[Personne: #x]',
      '[Assertion: #121‹]-(00.Denotatum)->[>MEDECIN<]',
    ],
    // No tag: in an MMG practice every consultation is MMG, so a sidebar tag
    // would be redundant (ALDO-2540). Explicit `undefined` overrides the `tag:
    // 'SNP'` inherited from `...snp` above.
    tag: undefined,
  },
  mmg_borne: {
    ...snp,
    name: 'Maison médicale de garde (borne)',
    status: 'beta',
    availableInDemo: false,
    description:
      "Questionnaire pour les Maisons Médicales de Garde (MMG) sur borne / tablette partagée en salle d'attente. Onboarding kiosque sans e-mail (démarrage immédiat) et confirmation directe en sortie. La page de consultation s'arrête après l'adressage : la borne ne recueille que l'identité et l'adressage, le patient est traité comme un nouveau patient.",
    patientOnboarding: 'BORNE',
    patientOffboarding: 'DIRECT_CONFIRMATION',
    // ADDRESSAGE_MMG : page simplifiée + adressage #MMG01.
    // MMG_BORNE : arrête le questionnaire après #MMG01 (rien d'autre n'est posé).
    options: [...snp.options, 'ADDRESSAGE_MMG', 'MMG_BORNE'],
    initialAssertions: [
      ...snp.initialAssertions,
      // #22=Non (le médecin de garde n'est pas le médecin traitant) — miroir 22-02.
      '[Conjonction: #1‹]-(ET)->[Assertion: #12‹]',
      '[Assertion: #12‹]-(01.DESCR)->[Médecin: #z]-(---¬)->[Médecin: #]-(rôle)->[Médecin Traitant: #]-(REL)->[Personne: #x]',
      '[Assertion: #121‹]-(00.Denotatum)->[>MEDECIN<]',
      // #21=Oui (nouveau patient) — UNIQUEMENT les assertions de classification
      // patient (miroir 21-01). On n'injecte PAS MOTIVES_ENABLED / PREVENTION_* /
      // SCREENING_* : sans ces marqueurs de routage, aucune section ne s'ouvre et
      // le questionnaire s'arrête après l'adressage.
      '[Prestation: #00‹]-(a_occurrence)->[Occurrence: N=1]',
      '[Prestation: #99‹]-(a_occurrence)->[Occurrence: N=0]',
      '[Prestation: #00‹]-(PREC)->[Prestation: #99‹]',
      '[Prestation: #00‹]-(a_profil)->[Profil: Charlie]',
      '[ATCD]-(a_status)->[Red]',
    ],
    personalizableOptions: {},
    // No tag (see `mmg`): explicit `undefined` overrides the inherited `tag:
    // 'SNP'` from `...snp` (ALDO-2540).
    tag: undefined,
    // Dedicated static check-in report instead of the (empty) Med G sections.
    reportTemplate: 'MMG_BORNE',
    // The patient can continue the check-in on their own phone with the full
    // QR questionnaire, via the continuation SMS (ALDO-2759).
    continuationVariant: 'mmg',
  },
  med_g_hersange: {
    ...DEFAULT,
    name: 'Médecine générale Herserange',
    status: 'restricted',
    availableInDemo: false,
    description:
      'Questionnaire pour les patients de médecine générale de la MSP Herserange. Couvre les motifs, les antécédents personnels et familiaux du patient ainsi que la prévention. Demande le consentement du patient pour être recontacté dans le cadre des dépistages de la CPTS.',
    options: ['COMPACT', 'ALERTES', 'MSP_HERSANGE_CPTS_CONTACT_CONSENT'],
  },
  snp_hersange: {
    ...snp,
    name: 'Soins non programmés Herserange',
    status: 'restricted',
    availableInDemo: false,
    description:
      'Questionnaire adapté aux consultations sans rendez-vous ou urgences légères. Le patient sélectionne un seul motif parmi une liste ciblée pour permettre un recueil rapide et efficace avant la prise en charge. Demande le consentement du patient pour être recontacté dans le cadre des dépistages de la CPTS.',
    options: [
      'COMPACT',
      'ALERTES',
      'SINGLE_MOTIVE',
      'LIMITED_MOTIVES',
      'MSP_HERSANGE_CPTS_CONTACT_CONSENT',
    ],
  },
  journee_prevention_msp_espoirs: {
    name: 'Journée de prévention',
    status: 'deprecated',
    description:
      "Questionnaire pour la journée de prévention de la MSP Espoirs, centré sur les dépistages du cancer du sein, du cancer colorectal et du cancer du col de l'utérus.",
    speciality: 'MED-G',
    patientOnboarding: 'MED_G',
    patientOffboarding: 'DIRECT_CONFIRMATION',
    emailTemplate: 'MED_G_DEFAULT',
    reportTemplate: 'MED_G_DEFAULT',
    initialQuestions: ['123131P'],
    pages: {
      SCREENING: { title: 'Dépistage', sections: ['SCREENING'] },
    },
    initialAssertions: [
      '[Assertion: #12‹]-(01.DESCR)->[Médecin: #z]-(----)->[Médecin: #]-(rôle)->[Médecin Traitant: #z]-(REL)->[Personne: #x]',
      '[Assertion: #50‹]-(01.DESCR)->[Patient: #x]-(----)->[Personne: #]-(CIBLE-1)->[Dépistage]-(01.PAT)->[Cancer]-(LOC)->[[Colon]v[Rectum]]',
    ],
    options: ['COMPACT', 'ALERTES', 'JOURNEE_PREVENTION_MSP_ESPOIRS'],
  },
} satisfies Variants;
